import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { participationsApi } from '@/api/participations.api';
import { AddParticipant, Participant, ParticipantContext, ParticipationStats } from '@/types/participant';
import { averageScores, minMaxScores } from '@/types/results';
import { storeToRefs } from 'pinia';
import { camelize } from 'vue';

export function useParticipations(isOnboarded = false) {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['participations', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: appStore.currentCampaignId }],
    queryFn: () => participationsApi.getAll({ organisationId: toValue(appStore.currentOrganisationId), spaceId: toValue(appStore.currentSpaceId.value), campaignId: toValue(appStore.currentCampaignId.value) }, isOnboarded),
    refetchInterval: 60_000, // 1 minute
    enabled: !!toValue(appStore.currentOrganisationId) && !!toValue(appStore.currentSpaceId) && !!toValue(appStore.currentCampaignId)
  });
}

function computeStats(participations: Participant[]) {
  let stats: ParticipationStats = {
    averageCompatibilityScore: 0,
    averageScores: null,
    minMaxScores: {
      min: undefined,
      max: undefined
    },
    numberOfParticipants: 0,
  };
  const finishedParticipants = participations.filter(p => p.state === 2 && p?.results);

  stats.averageCompatibilityScore = finishedParticipants.reduce((acc, p) => {
    acc = acc + p.results.compatibility;
    return acc;
  }, 0) / finishedParticipants.length;
  stats.averageScores = averageScores(finishedParticipants.map(p => p.results.scores));
  stats.minMaxScores = minMaxScores(finishedParticipants.map(p => p.results.scores));
  stats.numberOfParticipants = participations.length;
  return stats;
}

export function useParticipationsStats() {
  const appStore = storeToRefs(useAppStore());
  const { data: participations, isSuccess } = useParticipations();

  return useQuery({
    queryKey: ['participations', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: appStore.currentCampaignId }, 'stats'],
    queryFn: () => computeStats(participations.value),
    enabled: isSuccess,
    staleTime: Infinity,
  });
}

export function inviteTalent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { campaignId: string, email: string }) => participationsApi.invite(data.campaignId, data.email),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', { campaignId: variables.campaignId }] });
    }
  });
}

export function useAddParticipations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, inputs: AddParticipant[] }) => participationsApi.add(data.context, data.inputs),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}

export function useDeleteParticipations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantIds: string[] }) => participationsApi.delete(data.context, data.participantIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}

export function useActivateParticipations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantIds: string[] }) => participationsApi.activateParticipants(data.context, data.participantIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}

export function useDesactivateParticipations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantIds: string[] }) => participationsApi.desactivateParticipants(data.context, data.participantIds),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}


export function useMoveParticipations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantIds: string[], destinationCampaignId: string }) => participationsApi.moveParticipants(data.context, data.participantIds, data.destinationCampaignId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}

export function useCommentParticipation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantId: string, comment: string }) => participationsApi.commentParticipant(data.context, data.participantId, data.comment),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['participations', variables.context] });
    }
  });
}

export function useRemindParticipation() {
  return useMutation({
    mutationFn: (data: { context: ParticipantContext, participantId: string }) => participationsApi.sendReminderToParticipant(data.context, data.participantId),
  });
}